/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2018
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
/**
 * A simple and generic LRU map implementation. ONE.core uses it for an internal ID hash cache.
 * This utility module can be used by anyone, there is nothing specific to ONE in it.
 * @module
 */
import { createError } from '../errors.js';
import { isInteger } from './type-checks-basic.js';
/**
 * Creates an LRU collection with a given maximum size.
 * Also see {@link LruMapObj}
 * @static
 * @param {number} maxSize - The maximum size of the LRU collection
 * @returns {LruMapObj} Returns an LRU-Map object
 */
export function createLruMap(maxSize) {
    if (!isInteger(maxSize) || maxSize < 2) {
        throw createError('ULRU-CRM1', { maxSize, type: typeof maxSize });
    }
    const collection = new Map();
    function get(key) {
        const entry = collection.get(key);
        if (entry !== undefined) {
            collection.delete(key);
            collection.set(key, entry);
        }
        return entry;
    }
    function set(key, value) {
        if (collection.has(key)) {
            // Refresh this entry. This also ensures that if the collection is full no other
            // entry is removed.
            collection.delete(key);
        }
        else if (collection.size >= maxSize) {
            // Since we always reinsert upon use (get or set), and since the Map iterator uses
            // insertion order, the first item it returns will be the least recently used one.
            const firstKey = collection.keys().next().value;
            if (firstKey !== undefined) {
                collection.delete(firstKey);
            }
        }
        collection.set(key, value);
    }
    function clear() {
        collection.clear();
    }
    return {
        [Symbol.iterator]() {
            return collection.entries();
        },
        get,
        set,
        clear
    };
}
//# sourceMappingURL=lru-map.js.map